import { useLDFlag } from "launchdarkly-vue-client-sdk";
import { computed, type ComputedRef } from "vue";
import { helloEmail, isUS } from "@/globals";
import {
  getMedicalQuestionnaireDeferCompletionMessage,
  getStripeFinancialOperationsUnavailableMessage
} from "utils/stripeFinancialOperationsMaintenance";
import {
  memberDigitalReportViaReporter,
  myEzraBlockedBookingPackageIds,
  stripeFunctionHealthPrimaryForNewActions
} from "@/utils/launchDarkly/ldFeatureKeys";

/**
 * Simple feature flags composable for MyEzra.
 * Provides direct access to single-purpose LaunchDarkly feature flags.
 */
export function useFeatureFlags() {
  const oursPrivacyId = useLDFlag<string>("ours-privacy-id", "");
  const maintenanceDetails = useLDFlag<string>("maintenance-details", "");
  const consentCenterEnabled = useLDFlag<boolean>("consent-center", false);
  const functionSsoEnabled = useLDFlag<boolean>(
    "function-sso-enabled",
    !!import.meta.env.VITE_APP_FUNCTION_HEALTH_URL
  );
  const bodySpecDexaEnabled = useLDFlag<boolean>(
    "bodyspec-dexa-enabled",
    false
  );
  const blockedBookingPackageIdsFlag = useLDFlag<unknown>(
    myEzraBlockedBookingPackageIds,
    ""
  );
  const organBasedReportsEnabled = useLDFlag<boolean>(
    "organ-based-reports-enabled",
    false
  );
  const snippetFeaturesEnabled = useLDFlag<boolean>(
    "snippet-features-enabled",
    false
  );

  const stripeFinancialOperationsEnabled = useLDFlag<boolean>(
    "stripe-financial-operations-enabled",
    true
  );
  const functionHealthStripePrimaryForNewActions = useLDFlag<boolean>(
    stripeFunctionHealthPrimaryForNewActions,
    isUS
  );
  const memberDigitalReportViaReporterEnabled = useLDFlag<boolean>(
    memberDigitalReportViaReporter,
    false
  );

  const stripeFinancialOperationsUnavailableMessage = computed(() =>
    getStripeFinancialOperationsUnavailableMessage(
      helloEmail ?? "hello@ezra.com"
    )
  ) as ComputedRef<string>;

  const medicalQuestionnaireDeferCompletionMessage = computed(() =>
    getMedicalQuestionnaireDeferCompletionMessage(
      helloEmail ?? "hello@ezra.com"
    )
  ) as ComputedRef<string>;

  const blockedBookingPackageIds = computed(() =>
    typeof blockedBookingPackageIdsFlag.value === "string"
      ? blockedBookingPackageIdsFlag.value
          .split(",")
          .map(id => id.trim().toLowerCase())
          .filter(Boolean)
      : []
  ) as ComputedRef<string[]>;

  const oursEnabled = computed(
    () => !!oursPrivacyId.value?.trim()
  ) as ComputedRef<boolean>;

  return {
    oursPrivacyId,
    oursEnabled,
    maintenanceDetails,
    stripeFinancialOperationsEnabled,
    stripeFinancialOperationsUnavailableMessage,
    medicalQuestionnaireDeferCompletionMessage,
    consentCenterEnabled,
    functionSsoEnabled,
    functionHealthStripePrimaryForNewActions,
    bodySpecDexaEnabled,
    blockedBookingPackageIds,
    organBasedReportsEnabled,
    snippetFeaturesEnabled,
    memberDigitalReportViaReporterEnabled
  };
}

export type UseFeatureFlagsReturn = ReturnType<typeof useFeatureFlags>;
